<template>
  <main class="mb-10 text-black">
    <section class="flex justify-between flex-wrap items-center col-span-12">
      <div class="container mx-auto flex items-center justify-between">
        <div class="flex items-center gap-3">
          <h4>
            {{ $t("objectives.titles.objectives") }}
          </h4>
          <Button
            id="objective-add-btn"
            :label="$t('labels.add', { pt: 'objetivo', en: 'objective' })"
            class="btn-outline p-button-sm"
            icon="pi pi-plus"
            @click="goToObjective()"
          />
        </div>
        <Button
          id="objective-next-btn"
          :label="$t('labels.generic', { pt: 'Continuar', en: 'Continue' })"
          class="btn-primary p-button-sm"
          @click="saveObjectives()"
        />
      </div>
      <div class="container mx-auto mt-2" v-if="textError">
        <p class="text-h-error-400 text-sm">
          {{ textError }}
        </p>
      </div>

      <section class="container mx-auto mt-8">
        <InfiniteScroll>
          <template #header>
            <div
              class="grid grid-cols-12 items-center overflow-auto px-5 py-3 gap-5"
            >
              <span class="col-span-12 text-[18px] font-[700]">
                {{
                  $t("labels.generic", {
                    pt: "Lista de objetivos",
                    en: "List of objectives",
                  })
                }}
              </span>
            </div>
          </template>
          <template #content>
            <div class="flex gap-2 flex-col p-4">
              <ListObjective
                v-for="(objective, j) in objectives"
                :key="j"
                :objective="objective"
                :chapter="chapter"
                :courseId="params.courseId"
                @on-order="openMaterialsDialog"
                hasOrder
              />
              <EmptyData
                v-if="objectives.length === 0"
                :data="true"
                :title="`${$t('labels.generic', {
                  pt: 'Nenhum objetivo adicionado',
                  en: 'No objectives added',
                })}`"
                :label="`${$t('labels.generic', {
                  pt: 'Os objetivos ficarão listados aqui. Clique em “Adicionar objetivos” para ver esses itens.',
                  en: 'The objectives will be listed here. Click “Add objectives” to view these items.',
                })}`"
                :image="'empty-objectives.svg'"
                imgSize="h-40"
                class="mt-16"
              />
            </div>
          </template>
        </InfiniteScroll>
      </section>
    </section>

    <Dialog
      v-model:visible="dialogOrder"
      :draggable="false"
      :breakpoints="{ '960px': '95vw', '640px': '95vw' }"
      :style="{ width: '40vw', minWidth: '360px' }"
      :modal="true"
      :header="$t('order.header', { name: currentObjective.name })"
      @hide="onCloseOrder"
    >
      <ObjectiveList
        v-model:data="currentMaterials"
        v-model:loader="loading"
        :lesson-id="currentObjective.lessonPlan"
        @on-edit="goToEditObject"
        @on-delete="deleteObject"
      />
    </Dialog>
  </main>
</template>

<script lang="ts">
import { Chapter } from "@/models/chapter.model";
import { LearningObjective } from "@/models/learning-objective.model";
import { ConfigModal, Material } from "@/models/material.model";
import { MaterialsRest } from "@/services/rest/materials.rest";
import { GenericsUtils } from "@/utils/generics.util";
import { GoToRoute } from "@/utils/go-to.util";
import { defineAsyncComponent, defineComponent, PropType } from "vue";

const ObjectiveList = defineAsyncComponent(
  () => import("./objective-list.vue")
);

export default defineComponent({
  name: "objective",
  emits: ["onNext", "onAllow", "onUpdate"],
  components: { ObjectiveList },
  data() {
    return {
      params: {
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        objectiveId: "",
      },
      textError: "" as string,
      currentMaterials: [] as Material[],
      configModal: new ConfigModal(),
      currentObjective: {} as LearningObjective,
      dialogOrder: false as boolean,
      loading: false as boolean,
    };
  },
  props: {
    objectives: {
      type: Array as PropType<Array<LearningObjective>>,
      default: [""],
    },
    chapter: {
      type: Object as PropType<Chapter>,
      default: new Chapter(),
    },
  },
  computed: {
    go(): GoToRoute {
      return new GoToRoute();
    },
    generics(): GenericsUtils {
      return new GenericsUtils();
    },
    rest(): MaterialsRest {
      return new MaterialsRest();
    },
  },
  methods: {
    goToObjective(objectiveId: string = ""): void {
      if (objectiveId) {
        this.params.objectiveId = objectiveId;
      }
      this.go.redirectToPage("formObjectiveTraditional", this.params);
    },
    goToMaterials(objectiveId: string): void {
      let params = {
        objectiveId: objectiveId,
        type: "add",
        courseId: this.params.courseId,
      };
      this.go.redirectToPage("formObjectTraditional", params);
    },
    saveObjectives(): void {
      if (this.objectives.length > 0) {
        this.objectives.forEach((element) => {
          if (element.materials && element.materials?.length > 0) {
            this.$emit("onNext", 2);
            this.$emit("onAllow", 1);
          } else {
            this.textError = this.$t("labels.generic", {
              pt: `Por favor adicionar ao menos um objeto`,
              en: `Please add at least one material`,
            });
          }
        });
      } else {
        this.textError = this.$t("labels.generic", {
          pt: `Por favor adicionar ao menos um objetivo e um objeto`,
          en: `Please add at least one object and one object`,
        });
      }
    },
    openMaterialsDialog(event: LearningObjective): void {
      this.dialogOrder = true;
      this.currentMaterials = event.materials!;
      this.currentObjective = event;
    },
    onCloseOrder(): void {
      this.dialogOrder = false;
      this.$emit("onUpdate");
    },
    goToEditObject(material: any): void {
      const params = {
        objectiveId: this.currentObjective.id,
        id: material.id,
        type: "edit",
      };
      this.go.redirectToPage("formObjectTraditional", params);
    },
    deleteObject(material: Material) {
      this.loading = true;
      this.rest
        .deleteMaterial(this.currentObjective.id, material.id)
        .subscribe({
          next: () => {
            const materials = this.currentMaterials;
            const filtered = materials.filter((e) => e.id !== material.id);
            this.currentMaterials = filtered;
          },
          error: () => {
            this.loading = false;
          },
          complete: () => {
            this.loading = false;
          },
        });
    },
  },
});
</script>
